import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { authService } from "../services/authService";
import { companyApi } from "../services/companyApi";
import { Company } from "../types";

interface CompanyContextType {
  company: Company | null;
  companies: Company[];
  setCompany: (company: Company | null) => void;
  addCompany: (company: Company) => void;
  /** Update a company in the list and in current selection if it's the selected company */
  updateCompany: (company: Company) => void;
  /** GET /companies, persiste em `user_data` e atualiza o estado (navbar). */
  refreshCompaniesFromApi: () => Promise<void>;
  isLoading: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({
  children,
}) => {
  const [company, setCompanyState] = useState<Company | null>(null);
  const [companies, setCompaniesState] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCompanyData();
  }, []);

  const loadCompanyData = () => {
    setIsLoading(true);
    try {
      const companiesList = authService.getCompanies();
      setCompaniesState(companiesList);
      let current = authService.getCompanyData();
      if (companiesList.length > 0 && !current) {
        current = companiesList[0];
        authService.setCompanyData(current);
      }
      setCompanyState(current);
    } catch (error) {
      console.error("Failed to load company data:", error);
      setCompanyState(null);
      setCompaniesState([]);
    } finally {
      setIsLoading(false);
    }
  };

  const setCompany = (companyData: Company | null) => {
    setCompanyState(companyData);
    authService.setCompanyData(companyData);
  };

  const addCompany = (newCompany: Company) => {
    authService.addCompanyToUser(newCompany);
    setCompaniesState((prev) =>
      prev.some((c) => c.id === newCompany.id) ? prev : [...prev, newCompany]
    );
  };

  const updateCompany = (updatedCompany: Company) => {
    authService.updateCompanyInList(updatedCompany);
    setCompaniesState((prev) => {
      if (prev.some((c) => c.id === updatedCompany.id)) {
        return prev.map((c) =>
          c.id === updatedCompany.id ? updatedCompany : c
        );
      }
      return [...prev, updatedCompany];
    });
    setCompanyState((prev) =>
      prev?.id === updatedCompany.id ? updatedCompany : prev
    );
  };

  const refreshCompaniesFromApi = useCallback(async () => {
    const res = await companyApi.listCompanies();
    if (!res.success || !res.data) return;
    const list = res.data;
    if (list.length === 0) {
      setCompaniesState([]);
      return;
    }
    authService.setUserCompanies(list);
    setCompaniesState(list);
    const current = authService.getCompanyData();
    const selectedId = current?.id;
    const next =
      selectedId != null ? list.find((c) => c.id === selectedId) ?? list[0] : list[0];
    setCompanyState(next);
    authService.setCompanyData(next);
  }, []);

  const value: CompanyContextType = {
    company,
    companies,
    setCompany,
    addCompany,
    updateCompany,
    refreshCompaniesFromApi,
    isLoading,
  };

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
};
